// import { NovelStore } from './stores/novelStore'
// import { ChapterStore } from './stores/chapterStore'
// import { GenreStore } from './stores/genreStore'
// import { TagStore } from './stores/tagStore'
// import { OutlineStore } from './stores/outlineStore'
// import { CharacterStore } from './stores/characterStore'
import { StatsStore } from './stores/statsStore'
import {
  CodexCategoryStore,
  CodexFieldStore,
  CodexEntryStore,
  PromptStore,
  PromptDefaultStore,
  PromptPersonaStore,
  PromptPresetStore,
  ModelBankStore,
  ModelBankLinkStore,
  NovelStore,
  NovelSceneStore,
  NovelActStore,
  NovelChapterStore,
  NovelSeriesStore,
  SnippetStore,
  ChatThreadStore,
  ChatMessageStore,
} from './stores/novelCoreStore'



/**
 * 数据库管理器
 * 整合所有存储模块，提供统一的数据库操作接口
 */
export class DatabaseManager {
  private codexCategoryStore: CodexCategoryStore
  private codexFieldStore: CodexFieldStore
  private promptStore: PromptStore
  private codexEntryStore: CodexEntryStore
  private statsStore: StatsStore
  private promptDefaultStore: PromptDefaultStore
  private promptPersonaStore: PromptPersonaStore
  private promptPresetStore: PromptPresetStore
  private modelBankStore: ModelBankStore
  private modelBankLinkStore: ModelBankLinkStore
  private novelStore: NovelStore
  private novelSceneStore: NovelSceneStore
  private novelActStore: NovelActStore
  private novelChapterStore: NovelChapterStore
  private novelSeriesStore: NovelSeriesStore
  private snippetStore: SnippetStore
  private chatThreadStore: ChatThreadStore
  private chatMessageStore: ChatMessageStore

  private db: IDBDatabase | null = null
  private isInitialized = false
  private readonly DB_NAME = 'TaotaoWritingDB'
  private readonly DB_VERSION = 4

  constructor() {
    // 初始化所有存储模块
    this.codexCategoryStore = new CodexCategoryStore(null)
    this.codexFieldStore = new CodexFieldStore(null)
    this.codexEntryStore = new CodexEntryStore(null)
    this.promptStore = new PromptStore(null)
    this.statsStore = new StatsStore(null)
    this.promptDefaultStore = new PromptDefaultStore(null)
    this.promptPersonaStore = new PromptPersonaStore(null)
    this.promptPresetStore = new PromptPresetStore(null)
    this.modelBankStore = new ModelBankStore(null)
    this.modelBankLinkStore = new ModelBankLinkStore(null)
    this.novelStore = new NovelStore(null)
    this.novelSceneStore = new NovelSceneStore(null)
    this.novelActStore = new NovelActStore(null)
    this.novelChapterStore = new NovelChapterStore(null)
    this.novelSeriesStore = new NovelSeriesStore(null)
    this.snippetStore = new SnippetStore(null)
    this.chatThreadStore = new ChatThreadStore(null)
    this.chatMessageStore = new ChatMessageStore(null)
  }

  /**
   * 初始化数据库
   */
  async initializeDatabase(): Promise<void> {
    try {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(this.DB_NAME, this.DB_VERSION)

        request.onerror = () => {
          console.error('数据库打开失败:', request.error)
          reject(request.error)
        }

        request.onsuccess = () => {
          this.db = request.result
          this.isInitialized = true

          // 更新所有存储模块的数据库连接
          this.codexCategoryStore.setDatabase(this.db)
          this.codexFieldStore.setDatabase(this.db)
          this.codexEntryStore.setDatabase(this.db)
          this.promptStore.setDatabase(this.db)
          this.promptDefaultStore.setDatabase(this.db)
          this.promptPersonaStore.setDatabase(this.db)
          this.promptPresetStore.setDatabase(this.db)
          this.modelBankStore.setDatabase(this.db)
          this.modelBankLinkStore.setDatabase(this.db)
          this.statsStore.setDatabase(this.db)
          this.novelStore.setDatabase(this.db)
          this.novelSceneStore.setDatabase(this.db)
          this.novelActStore.setDatabase(this.db)
          this.novelChapterStore.setDatabase(this.db)
          this.novelSeriesStore.setDatabase(this.db)
          this.snippetStore.setDatabase(this.db)
          this.chatThreadStore.setDatabase(this.db)
          this.chatMessageStore.setDatabase(this.db)
          console.log('数据库初始化成功')
          resolve()
        }

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result as IDBDatabase
          this.createObjectStores(db)
        }
      })
    } catch (error) {
      console.error('数据库初始化失败:', error)
      throw error
    }
  }

  /**
   * 创建对象存储
   */
  private createObjectStores(db: IDBDatabase) {
    this.codexCategoryStore.createObjectStore(db)
    this.codexFieldStore.createObjectStore(db)
    this.codexEntryStore.createObjectStore(db)
    this.promptStore.createObjectStore(db)
    this.promptDefaultStore.createObjectStore(db)
    this.promptPersonaStore.createObjectStore(db)
    this.promptPresetStore.createObjectStore(db)
    this.modelBankStore.createObjectStore(db)
    this.modelBankLinkStore.createObjectStore(db)
    this.statsStore.createObjectStore(db)
    this.novelStore.createObjectStore(db)
    this.novelSceneStore.createObjectStore(db)
    this.novelActStore.createObjectStore(db)
    this.novelChapterStore.createObjectStore(db)
    this.novelSeriesStore.createObjectStore(db)
    this.snippetStore.createObjectStore(db)
    this.chatThreadStore.createObjectStore(db)
    this.chatMessageStore.createObjectStore(db)
    console.log('对象存储创建完成')
  }

  /**
   * 初始化默认数据
   */
  async initializeDefaultData(): Promise<void> {
    try {
      // 检查是否已有数据
      console.log('默认数据初始化完成')
    } catch (error) {
      console.error('默认数据初始化失败:', error)
      throw error
    }
  }

  /**
   * 完整初始化数据库（包括默认数据）
   */
  async fullInitialize(): Promise<void> {
    try {
      console.log('开始完整初始化数据库...')

      // 初始化数据库连接
      await this.initializeDatabase()

      // 初始化默认数据
      await this.initializeDefaultData()

      console.log('数据库完整初始化完成')
    } catch (error) {
      console.error('数据库完整初始化失败:', error)
      throw error
    }
  }

  /**
   * 关闭数据库连接
   */
  closeDatabase(): void {
    if (this.db) {
      this.db.close()
      this.db = null
      this.isInitialized = false
      console.log('数据库连接已关闭')
    }
  }

  /**
   * 重置数据库（删除所有数据并重新初始化）
   */
  async resetDatabase(): Promise<void> {
    try {
      console.log('开始重置数据库...')

      // 关闭现有连接
      this.closeDatabase()

      // 删除数据库
      await this.deleteDatabase()

      // 重新初始化
      await this.fullInitialize()

      console.log('数据库重置完成')
    } catch (error) {
      console.error('数据库重置失败:', error)
      throw error
    }
  }

  /**
   * 删除数据库
   */
  private async deleteDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(this.DB_NAME)

      request.onerror = () => {
        console.error('删除数据库失败:', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        console.log('数据库删除成功')
        resolve()
      }
    })
  }

  /**
   * 检查数据库是否存在
   */
  async checkDatabaseExists(): Promise<boolean> {
    return new Promise((resolve) => {
      const request = indexedDB.open(this.DB_NAME)

      request.onerror = () => resolve(false)
      request.onsuccess = () => {
        request.result.close()
        resolve(true)
      }
    })
  }

  /**
   * 获取数据库版本信息
   */
  async getDatabaseVersion(): Promise<number> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        const version = request.result.version
        request.result.close()
        resolve(version)
      }
    })
  }

  /**
   * 检查数据库是否已初始化
   */
  isReady(): boolean {
    return this.isInitialized
  }

  // 代码库相关操作
  get codexCategories() {
    return this.codexCategoryStore
  }

  // 代码库字段相关操作
  get codexFields() {
    return this.codexFieldStore
  }

  // 代码库条目相关操作
  get codexEntries() {
    return this.codexEntryStore
  }

  // 提示词相关操作
  get prompts() {
    return this.promptStore
  }

  // 提示词默认相关操作
  get promptDefaults() {
    return this.promptDefaultStore
  }

  // 提示词人物相关操作
  get promptPersonas() {
    return this.promptPersonaStore
  }

  // 提示词预设相关操作
  get promptPresets() {
    return this.promptPresetStore
  }

  // 模型库相关操作
  get modelBanks() {
    return this.modelBankStore
  }

  // 模型库链接相关操作
  get modelBankLinks() {
    return this.modelBankLinkStore
  }


  // 小说相关操作
  get novels() {
    return this.novelStore
  }

  // 小说场景相关操作
  get novelScenes() {
    return this.novelSceneStore
  }

  // 小说章节相关操作
  get novelActs() {
    return this.novelActStore
  }

  // 小说章节相关操作
  get novelChapters() {
    return this.novelChapterStore
  }

  // 小说系列相关操作
  get novelSeries() {
    return this.novelSeriesStore
  }

  // 统计相关操作
  get stats() {
    return this.statsStore
  }

  // 片段相关操作
  get snippets() {
    return this.snippetStore
  }

  // 聊天相关操作
  get chatThreads() {
    return this.chatThreadStore
  }

  // 聊天消息相关操作
  get chatMessages() {
    return this.chatMessageStore
  }

  /**
   * 清空所有数据
   */
  async clearAllData(): Promise<void> {
    try {
      await Promise.all([
        this.codexEntryStore.clearData(),
        this.codexFieldStore.clearData(),
        this.codexCategoryStore.clearData(),
        this.promptStore.clearData(),
        this.promptDefaultStore.clearData(),
        this.promptPersonaStore.clearData(),
        this.promptPresetStore.clearData(),
        this.modelBankStore.clearData(),
        this.modelBankLinkStore.clearData(),
        this.statsStore.clearData(),
        this.novelStore.clearData(),
        this.novelSceneStore.clearData(),
        this.novelActStore.clearData(),
        this.novelChapterStore.clearData(),
        this.novelSeriesStore.clearData(),
        this.snippetStore.clearData(),
        this.chatThreadStore.clearData(),
        this.chatMessageStore.clearData(),
      ])
      console.log('所有数据已清空')
    } catch (error) {
      console.error('清空数据失败:', error)
      throw error
    }
  }
} 